/**
 * GOAL 2026-09-17 HS-7 — DATA-USE PERMISSIONS for the historical scenario library.
 *
 * "Licensed" is one of the four readiness states a scenario needs before it can
 * be offered (source-complete, engine-capable, licensed, human-reviewed), and it
 * is judged per SOURCE here, never per scenario by hand: a scenario is licensed
 * only when every source its replay reads is `cleared` for this commercial
 * product. Research availability is not commercial-use clearance.
 *
 * `cleared` is recorded only where the publisher's own terms grant commercial
 * reuse (the Open Government Licence). Everything else is `not-cleared` until
 * the owner holds a written licence or permission — and then the evidence
 * reference goes here and in docs/security/historical-scenario-permissions.md,
 * in the same commit. No code path upgrades a source's status.
 */
const OGL = 'Open Government Licence v3.0';
/** Every level frozen: a status cannot be flipped at runtime (tech-admin review). */
function deepFreeze(value) {
    if (value !== null && typeof value === 'object' && !Object.isFrozen(value)) {
        Object.freeze(value);
        for (const v of Object.values(value))
            deepFreeze(v);
    }
    return value;
}
const REVIEWED = '2026-09-17';
export const HISTORICAL_SOURCE_PERMISSIONS = deepFreeze({
    S01: {
        commercialUse: 'not-cleared',
        terms: 'CC BY-NC-SA 4.0 (Jordà–Schularick–Taylor Macrohistory Database)',
        evidence: `https://www.macrohistory.net/database/ (read ${REVIEWED}): commercial data providers are "strictly forbidden to integrate all or parts of the dataset into their services".`,
        attribution: 'Òscar Jordà, Moritz Schularick and Alan M. Taylor (2017); return data Jordà, Knoll, Kuvshinov, Schularick and Taylor (2019).',
        toClear: 'A licensed commercial replacement for pre-2008 UK equity, gilt and house-price total returns (the database terms do not allow integration into a commercial service).',
    },
    S02: {
        commercialUse: 'not-cleared',
        terms: 'Bank of England research dataset "A millennium of macroeconomic data" — no licence stated on the dataset page; the website terms limit reuse to personal or internal non-commercial use',
        evidence: `https://www.bankofengland.co.uk/statistics/research-datasets and https://www.bankofengland.co.uk/legal (read ${REVIEWED}).`,
        attribution: 'Bank of England, A millennium of macroeconomic data, v3.1.',
        toClear: 'Written permission from the Bank of England for commercial reuse of the workbook, or a replacement from the Bank of England Database (Open Government Licence) where an equivalent series exists.',
    },
    S03: {
        commercialUse: 'not-cleared',
        terms: 'Documentation for S01 — not a numeric input',
        evidence: 'Method documentation only.',
        attribution: null,
        toClear: 'Not needed: documentation is not read by the replay.',
    },
    S04: {
        commercialUse: 'not-cleared',
        terms: 'FTSE Russell factsheet — "No part of this information may be reproduced … without prior written permission"; use of index data requires a licence',
        evidence: `FTSE Russell factsheet notices (read ${REVIEWED}).`,
        attribution: 'FTSE Russell, FTSE All-Share Index.',
        toClear: 'A data licence from FTSE Russell (LSEG) covering FTSE All-Share total returns.',
    },
    S05: {
        commercialUse: 'not-cleared',
        terms: 'FTSE Russell factsheet — reproduction requires prior written permission; use of index data requires a licence',
        evidence: `FTSE Russell factsheet notices (read ${REVIEWED}).`,
        attribution: 'FTSE Russell, FTSE All-Share Index.',
        toClear: 'A data licence from FTSE Russell (LSEG) covering FTSE All-Share total returns.',
    },
    S06: {
        commercialUse: 'not-cleared',
        terms: 'BlackRock fund document reporting the FTSE Actuaries UK Conventional Gilts All Stocks index (third-party index data)',
        evidence: `iShares UK Gilts All Stocks Index Fund KIID, 18 June 2018 (read ${REVIEWED}).`,
        attribution: 'FTSE Russell, FTSE Actuaries UK Conventional Gilts All Stocks Index.',
        toClear: 'A data licence from FTSE Russell (LSEG) covering the FTSE Actuaries UK Conventional Gilts All Stocks index.',
    },
    S07: {
        commercialUse: 'not-cleared',
        terms: 'BlackRock factsheet reporting the FTSE Actuaries UK Conventional Gilts All Stocks (Midday) index (third-party index data)',
        evidence: `iShares UK Gilts All Stocks Index Fund Class H factsheet, August 2026 (read ${REVIEWED}).`,
        attribution: 'FTSE Russell, FTSE Actuaries UK Conventional Gilts All Stocks (Midday) Index.',
        toClear: 'A data licence from FTSE Russell (LSEG) covering the FTSE Actuaries UK Conventional Gilts All Stocks index.',
    },
    S08: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: 'HM Land Registry UK House Price Index open data, published under the OGL.',
        attribution: 'Contains HM Land Registry data © Crown copyright and database right.',
        toClear: null,
    },
    S09: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: 'Office for National Statistics time series D7G7 / D7BT, published under the OGL.',
        attribution: 'Source: Office for National Statistics licensed under the Open Government Licence v3.0.',
        toClear: null,
    },
    S10: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: 'Office for National Statistics time series CZBH, published under the OGL.',
        attribution: 'Source: Office for National Statistics licensed under the Open Government Licence v3.0.',
        toClear: null,
    },
    S11: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: `Bank of England Database is published under the UK Open Government Licence (https://www.bankofengland.co.uk/legal, read ${REVIEWED}).`,
        attribution: 'Bank of England, Official Bank Rate history.',
        toClear: null,
    },
    S12: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: `Bank of England Database, series IUMB6VJ (OGL, https://www.bankofengland.co.uk/legal, read ${REVIEWED}).`,
        attribution: 'Bank of England Database, series IUMB6VJ.',
        toClear: null,
    },
    S13: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: `Bank of England Database, series IUMTLMV (OGL, read ${REVIEWED}).`,
        attribution: 'Bank of England Database, series IUMTLMV.',
        toClear: null,
    },
    S14: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: `Bank of England Database, series IUMBV34 (OGL, read ${REVIEWED}).`,
        attribution: 'Bank of England Database, series IUMBV34.',
        toClear: null,
    },
    S15: {
        commercialUse: 'not-cleared',
        terms: 'OECD data via FRED — context only, not read by the replay',
        evidence: 'Gilt yields are diagnostic context and are not shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read gilt yields.',
    },
    S16: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: 'Office for National Statistics time series KAB9, published under the OGL.',
        attribution: 'Source: Office for National Statistics licensed under the Open Government Licence v3.0.',
        toClear: null,
    },
    S17: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: 'Office for National Statistics time series IHYP — context only.',
        attribution: 'Source: Office for National Statistics licensed under the Open Government Licence v3.0.',
        toClear: null,
    },
    S18: {
        commercialUse: 'cleared',
        terms: OGL,
        evidence: 'Office for National Statistics time series MGSX — context only.',
        attribution: 'Source: Office for National Statistics licensed under the Open Government Licence v3.0.',
        toClear: null,
    },
    S19: {
        commercialUse: 'not-cleared',
        terms: 'Federal Reserve data via FRED — context only, not read by the replay',
        evidence: 'Exchange rates are diagnostic context and are not shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read exchange rates.',
    },
    S20: {
        commercialUse: 'not-cleared',
        terms: 'MSCI factsheet — analytics and redistribution restricted',
        evidence: `MSCI World Index (GBP) factsheet notices (read ${REVIEWED}).`,
        attribution: 'MSCI Inc.',
        toClear: 'A data licence from MSCI covering World, ACWI and Emerging Markets GBP gross returns.',
    },
    S21: {
        commercialUse: 'not-cleared',
        terms: 'Bank of England gilt-market case study — narrative context only, not read by the replay',
        evidence: 'Background reading cited by the research document; no figure is shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read it.',
    },
    S22: {
        commercialUse: 'not-cleared',
        terms: 'Federal Reserve History essay — narrative context only, not read by the replay',
        evidence: 'Background reading cited by the research document; no figure is shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read it.',
    },
    S23: {
        commercialUse: 'not-cleared',
        terms: 'Federal Reserve History essay — narrative context only, not read by the replay',
        evidence: 'Background reading cited by the research document; no figure is shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read it.',
    },
    S24: {
        commercialUse: 'not-cleared',
        terms: 'Bank of Japan speech — narrative context only, not read by the replay',
        evidence: 'Background reading cited by the research document; no figure is shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read it.',
    },
    S25: {
        commercialUse: 'not-cleared',
        terms: 'Bank of England history pages — narrative context only, not read by the replay',
        evidence: 'Background reading cited by the research document; no figure is shipped to the application.',
        attribution: null,
        toClear: 'Not needed: the replay does not read it.',
    },
});
/** A source the register does not list is not cleared — fail closed. */
export function isSourceCleared(sourceId, permissions = HISTORICAL_SOURCE_PERMISSIONS) {
    return permissions[sourceId]?.commercialUse === 'cleared';
}
//# sourceMappingURL=historical-source-permissions.js.map